import * as Yup from "yup";

/**
 * The one place the password rules live on the frontend.
 *
 * They used to be re-typed in four components (AcceptInvitation, AcceptInvitationNewPatientSelfSignup,
 * ResetPassword, PasswordSection), with the API holding a fifth copy and the Cognito user pool a
 * sixth — and they had drifted apart. The form and the API both required 8 characters while the
 * pool required 12, so a 9-character password passed every check the app made and was then rejected
 * by Cognito, *after* submission. The user saw a bare error message with no way back to the form.
 *
 * The symbol set was wrong too: the form's regex accepted only `!@#$%^&*(),.?":{}|<>`, while the API
 * (and Cognito) also allow `- _ + = [ ] \ / ' ; ~ \``. A password whose only symbol was a hyphen was
 * rejected by the form as having no special character, though it was perfectly valid.
 *
 * MUST stay in sync with:
 *   - MTC.Api/Services/PasswordPolicy.cs   (the API's validation)
 *   - deploy/provision.ts, cognito step    (the Cognito user pool policy)
 *
 * A test in MTC.Api.Tests (PasswordPolicyTests) reads this file and fails if the numbers drift.
 * The app may be stricter than the pool, never more permissive: the pool has the last word, and it
 * gets that word after the form is gone.
 */
export const passwordPolicy = {
  minLength: 12,
  maxLength: 40,
  /** Cognito's allowed symbol set, verbatim. */
  symbols: "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~",
} as const;

/** Human-readable requirements, for rendering next to the field rather than after a failure. */
export const passwordRequirements: string[] = [
  `At least ${passwordPolicy.minLength} characters`,
  "An uppercase letter",
  "A lowercase letter",
  "A number",
  "A special character",
];

const symbolClass = `[${passwordPolicy.symbols.replace(/[\]\\^-]/g, "\\$&")}]`;

export function passwordSchema(): Yup.StringSchema {
  return Yup.string()
    .required("Required")
    .min(passwordPolicy.minLength, `Must be at least ${passwordPolicy.minLength} characters long`)
    .max(passwordPolicy.maxLength, `Must be no more than ${passwordPolicy.maxLength} characters long`)
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(new RegExp(symbolClass), "Password must contain at least one special character");
}

/** The confirmation field: same rules, plus it must equal `ref`. */
export function confirmPasswordSchema(ref = "password"): Yup.StringSchema {
  return passwordSchema().oneOf([Yup.ref(ref)], "Passwords don't match!");
}
